import { useMemo, useState } from 'react'

import { usePermission } from '@circlefin/permissions/react'
import { settlementAdvanceSection } from '@services/sections/lib/settlementAdvance'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  SettlementCreditLineType,
  useSettlementCreditLinesQuery,
} from '@shared/graphql'

import { DEFAULT_VALUES, SettlementCreditLineContext } from './context'

import type { SettlementCreditLineContextState } from './context'

interface SettlementCreditLineProviderProps {
  /**
   * React Node Children.
   */
  children?: React.ReactNode
  /**
   * Overwrite default values of context. Primarily for testing.
   */
  initValues?: Partial<SettlementCreditLineContextState>
}

export const SettlementCreditLineProvider: React.FC<
  SettlementCreditLineProviderProps
> = ({ children, initValues }) => {
  const [, { isAuthorized, loading: permissionLoading }] = usePermission(
    settlementAdvanceSection.permissions,
  )
  const { data, loading, error, refetch } = useSettlementCreditLinesQuery({
    skip: !isAuthorized,
  })

  const settlementCreditLine = data?.settlementCreditLines[0]

  const [values] = useState<SettlementCreditLineContextState>({
    ...DEFAULT_VALUES,
    ...initValues,
  })

  const translationKey = useMemo(() => {
    switch (settlementCreditLine?.type) {
      case SettlementCreditLineType.lineOfCredit:
        return 'lineOfCredit'
      case SettlementCreditLineType.settlementAdvance:
      case undefined:
        return 'settlementAdvance'
    }
  }, [settlementCreditLine?.type])

  const context = useMemo(() => {
    return {
      ...values,
      loading: loading || permissionLoading,
      settlementCreditLine,
      settlementCreditLineType:
        settlementCreditLine?.type ??
        SettlementCreditLineType.settlementAdvance,
      translationKey,
    }
  }, [loading, permissionLoading, settlementCreditLine, translationKey, values])

  return (
    <SettlementCreditLineContext.Provider value={context}>
      <GraphQLErrorBoundary error={error} retry={refetch}>
        {children}
      </GraphQLErrorBoundary>
    </SettlementCreditLineContext.Provider>
  )
}
